import { Clock, Gauge, Lock, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/billing";
import { Button } from "@/components/ui/button";

export function CostChip({ cost, portions = 1, className = "" }) {
  const { t, lang } = useLang();
  if (!cost) return <span className={`rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground ${className}`}>{t("dm_cost_unavailable")}</span>;
  const amount = formatMoney(cost.per_serving * portions, cost.currency, lang);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 ${className}`} title={t("dm_cost_note", { converted: cost.converted ? t("dm_cost_converted") : "" })}>
      <span aria-hidden className="tracking-tighter">{"●".repeat(cost.band)}<span className="opacity-30">{"●".repeat(3 - cost.band)}</span></span>
      {portions === 1 ? t("dm_cost_per_serving", { amount }) : t("dm_cost_total", { amount, portions: String(portions).replace(".", ",") })}
    </span>
  );
}

export function TierBadge({ tier }) {
  const { t } = useLang();
  if (tier === "free") return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${tier === "premium" ? "bg-chenia-espresso text-chenia-cream" : "bg-chenia-rosesoft text-chenia-terracottadark"}`} data-testid={`demo-tier-${tier}`}>
      <Lock className="h-3 w-3" />{t(tier === "premium" ? "dm_tier_premium" : "dm_tier_plus")}
    </span>
  );
}

export function RecipeChips({ recipe, portions = 1 }) {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-900"><Clock className="h-3 w-3" />{recipe.total_time} {t("min")}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-900"><Gauge className="h-3 w-3" />{t(recipe.difficulty)}</span>
      <CostChip cost={recipe.cost} portions={portions} />
      <TierBadge tier={recipe.tier} />
    </div>
  );
}

export function DemoRecipeCard({ recipe, onView, onPick, picked, locked }) {
  const { t, tv } = useLang();
  return (
    <article className="chenia-card overflow-hidden flex flex-col" data-testid={`demo-recipe-${recipe.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img src={recipe.image} alt={tv(recipe.name)} loading="lazy" className="h-full w-full object-cover" />
        {(recipe.image_illustration || recipe.image_generic) && <span className="absolute bottom-2 left-2 rounded-full bg-black/45 px-2 py-0.5 text-[10px] text-white">{t(recipe.image_illustration ? "dm_illustration" : "dm_photo_generic")}</span>}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <RecipeChips recipe={recipe} />
        <h3 className="mt-3 font-serif text-lg font-semibold leading-snug">{tv(recipe.name)}</h3>
        {recipe.nutrition?.calories != null && <p className="mt-1 text-xs text-muted-foreground">{t("dm_kcal", { kcal: recipe.nutrition.calories })}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          <button type="button" onClick={() => onView(recipe)} className="text-sm font-medium text-primary underline underline-offset-4" data-testid={`demo-view-${recipe.id}`}>{t("dm_view_recipe")}</button>
          {onPick && (locked
            ? <span className="ml-auto text-xs text-muted-foreground">{t("dm_locked_hint", { plan: t(`plan_${recipe.tier}`) })}</span>
            : <Button size="sm" variant={picked ? "secondary" : "default"} onClick={() => onPick(recipe)} className="ml-auto rounded-full" data-testid={`demo-pick-${recipe.id}`}>
                {picked ? <><Check className="mr-1 h-3.5 w-3.5" />{t("dm_chosen")}</> : t("dm_pick")}
              </Button>)}
        </div>
      </div>
    </article>
  );
}
